import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert
} from '@mui/material';
import {
  CompareArrows,
  Schedule,
  CameraAlt
} from '@mui/icons-material';
import ComparisonHeader from './ComparisonHeader';
import ComparisonMap from './ComparisonMap';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';

const SplitMapComparison = ({
  selectedKeyword,
  rankingHistory = [],
  businessLocation,
  onCreateSnapshot,
  loading = false,
  Inpdf=false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [baselineDate, setBaselineDate] = useState('');
  const [comparisonDate, setComparisonDate] = useState('');
  const [mobileView, setMobileView] = useState('baseline'); // baseline | comparison
  const [availableDates, setAvailableDates] = useState([]);

  // Helper function für Datum-Validierung
  const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date);
  };

  // 🎯 DEDUPLICATION: Get latest valid snapshot per day for comparison mode
  const getLatestValidSnapshotPerDay = (snapshots, selectedKeyword) => {
    console.log('🔧 DEDUPLICATION: Processing snapshots for comparison mode', {
      totalSnapshots: snapshots.length,
      selectedKeyword
    });

    // For small snapshot counts (2-3), keep all snapshots instead of deduplicating by day
    // This allows comparison of multiple snapshots on the same day
    if (snapshots.length <= 3) {
      console.log('⏭️ DEDUPLICATION: Only 2-3 snapshots detected, skipping day grouping to preserve all measurements');

      const processedSnapshots = snapshots.map(entry => ({
        ...entry,
        dateValue: entry.analyzedAt || entry.snapshotDate,
        dateObj: new Date(entry.analyzedAt || entry.snapshotDate)
      })).filter(entry => isValidDate(entry.dateObj));

      // If keyword is selected, only keep snapshots that have the keyword
      if (selectedKeyword) {
        console.log(`🔎 DEDUPLICATION: Filtering ${processedSnapshots.length} snapshots for keyword "${selectedKeyword}"`);

        const filtered = processedSnapshots.filter(snapshot => {
          const dateStr = snapshot.dateObj.toLocaleDateString('de-DE');

          // Try exact match first
          let keywordInSnapshot = snapshot.keywords?.find(k => k.keyword === selectedKeyword);
          let keywordInMetrics = snapshot.keywordMetrics?.find(k => k.keyword === selectedKeyword);

          // If not found, try case-insensitive match
          if (!keywordInSnapshot) {
            keywordInSnapshot = snapshot.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
          }
          if (!keywordInMetrics) {
            keywordInMetrics = snapshot.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
          }

          const hasKeyword = !!(keywordInSnapshot || keywordInMetrics);

          console.log(`  📅 ${dateStr}: ${hasKeyword ? '✅' : '❌'} keyword "${selectedKeyword}" ${hasKeyword ? 'FOUND' : 'NOT FOUND'} (keywords: ${snapshot.keywords?.length || 0}, metrics: ${snapshot.keywordMetrics?.length || 0})`);
          return hasKeyword;
        });

        console.log(`🎯 DEDUPLICATION: Filtered to ${filtered.length} snapshots with keyword "${selectedKeyword}" (removed ${processedSnapshots.length - filtered.length})`);
        filtered.sort((a, b) => new Date(a.dateValue) - new Date(b.dateValue));
        return filtered;
      }

      processedSnapshots.sort((a, b) => new Date(a.dateValue) - new Date(b.dateValue));
      return processedSnapshots;
    }

    // For larger datasets, group by day to avoid overwhelming the UI
    // Group snapshots by day (YYYY-MM-DD format)
    const dayGroups = {};

    snapshots.forEach(entry => {
      const dateValue = entry.analyzedAt || entry.snapshotDate;
      const dateObj = new Date(dateValue);

      if (isValidDate(dateObj)) {
        const dayKey = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD

        if (!dayGroups[dayKey]) {
          dayGroups[dayKey] = [];
        }

        dayGroups[dayKey].push({
          ...entry,
          dateValue,
          dateObj
        });
      }
    });

    console.log(`🔧 DEDUPLICATION: Grouped into ${Object.keys(dayGroups).length} days`);

    // For each day, select the latest snapshot with valid keyword data
    const deduplicatedSnapshots = [];

    Object.keys(dayGroups).forEach(dayKey => {
      const daySnapshots = dayGroups[dayKey];

      // Sort by timestamp (latest first)
      daySnapshots.sort((a, b) => new Date(b.dateValue) - new Date(a.dateValue));

      console.log(`📅 DEDUPLICATION: Day ${dayKey} has ${daySnapshots.length} snapshots`);

      // Find the latest snapshot that contains the selected keyword
      let selectedSnapshot = null;

      if (selectedKeyword) {
        // When a keyword is selected, find snapshots with the keyword data
        for (const snapshot of daySnapshots) {
          // Try exact match first
          let keywordInSnapshot = snapshot.keywords?.find(k => k.keyword === selectedKeyword);
          let keywordInMetrics = snapshot.keywordMetrics?.find(k => k.keyword === selectedKeyword);

          // If not found, try case-insensitive match
          if (!keywordInSnapshot) {
            keywordInSnapshot = snapshot.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
          }
          if (!keywordInMetrics) {
            keywordInMetrics = snapshot.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
          }

          // Check if keyword exists (even with null/0 rank, as it's still valid data)
          const hasKeywordData = !!(keywordInSnapshot || keywordInMetrics);

          if (hasKeywordData) {
            selectedSnapshot = snapshot;
            const rank = keywordInSnapshot?.averageRank ?? keywordInMetrics?.averageRank ?? 'N/A';
            console.log(`✅ DEDUPLICATION: Selected snapshot with "${selectedKeyword}" data for ${dayKey} (rank: ${rank})`);
            break; // Take the first (latest) match
          }
        }

        // If no snapshot found with the keyword, skip this day entirely
        // Only show dates that have data for the selected keyword
        if (!selectedSnapshot) {
          console.log(`⚠️ DEDUPLICATION: Keyword "${selectedKeyword}" not found in any snapshot for ${dayKey}, skipping this date`);
          return; // Skip adding this day to deduplicatedSnapshots
        }
      } else {
        // No keyword selected, take the latest snapshot
        selectedSnapshot = daySnapshots[0];
        console.log(`✅ DEDUPLICATION: Selected latest snapshot (no keyword filter) for ${dayKey}`);
      }

      deduplicatedSnapshots.push(selectedSnapshot);
    });

    // Sort deduplicated snapshots by date
    deduplicatedSnapshots.sort((a, b) => new Date(a.dateValue) - new Date(b.dateValue));

    console.log(`🎯 DEDUPLICATION: Final result: ${deduplicatedSnapshots.length} unique days from ${snapshots.length} snapshots`);

    return deduplicatedSnapshots;
  };

  // Verfügbare Daten verarbeiten
  useEffect(() => {
    console.log('🔍 SPLIT MAP COMPARISON: Processing ranking history', {
      rankingHistoryLength: rankingHistory?.length || 0,
      rankingHistoryStructure: rankingHistory?.length > 0 ? Object.keys(rankingHistory[0]) : [],
      selectedKeyword
    });
    
    if (rankingHistory && rankingHistory.length > 0) {
      // 🎯 COMPARISON MODE: Apply deduplication to show only latest valid snapshot per day
      const deduplicatedHistory = getLatestValidSnapshotPerDay(rankingHistory, selectedKeyword);
      
      console.log('📊 SPLIT MAP: Deduplicated history entries:', deduplicatedHistory.map(entry => ({
        analyzedAt: entry.analyzedAt,
        snapshotDate: entry.snapshotDate,
        keywordsCount: entry.keywords?.length || entry.keywordMetrics?.length || 0,
        isSimulation: entry.isSimulation,
        dateValue: entry.dateValue
      })));
      
      const dates = deduplicatedHistory.map((entry, index) => {
        const dateValue = entry.dateValue; // Use the dateValue from deduplication
        const dateObj = entry.dateObj; // Use the dateObj from deduplication
        
        console.log(`📊 SPLIT MAP: Processing deduplicated entry ${index + 1}:`, {
          dateValue,
          dateString: dateObj.toLocaleDateString('de-DE'),
          keywordsCount: entry.keywords?.length || 0
        });
        
        return {
          value: dateValue,
          label: isValidDate(dateObj) ? dateObj.toLocaleDateString('de-DE') : 'Ungültiges Datum',
          data: entry,
          isSimulation: entry.isSimulation || entry.snapshotType === 'simulation'
        };
      }).filter(date => date.label !== 'Ungültiges Datum');
      
      console.log(`✅ SPLIT MAP: Processed ${dates.length} deduplicated dates from ${rankingHistory.length} history entries`);
      setAvailableDates(dates);

      // Smart Defaults: Älteste vs. Neueste für maximalen Vergleich
      // ALWAYS auto-select dates when ranking history or keyword changes
      if (dates.length >= 2) {
        console.log(`📌 AUTO-SELECT: Setting baseline to ${dates[0].label} and comparison to ${dates[dates.length - 1].label}`);
        setBaselineDate(dates[0].value); // Älteste (erste Simulation)
        setComparisonDate(dates[dates.length - 1].value); // Neueste (letzte Simulation)
      } else if (dates.length === 1) {
        console.warn(`⚠️ AUTO-SELECT: Only 1 date available (${dates[0].label}). Cannot compare - need minimum 2 dates with keyword "${selectedKeyword}"`);
        // Set both to the same date (will trigger "same snapshot" message)
        setBaselineDate(dates[0].value);
        setComparisonDate(dates[0].value);
        console.warn(`⚠️ Setting baseline and comparison to SAME date (${dates[0].label})`);
      } else {
        console.error(`❌ AUTO-SELECT: No dates available for keyword "${selectedKeyword}"`);
        // Reset dates if no data available
        setBaselineDate('');
        setComparisonDate('');
      }
    } else {
      console.log('⚠️ SPLIT MAP: No ranking history data available');
    }
  }, [rankingHistory, selectedKeyword]);

  // Baseline und Comparison Daten extrahieren
  const baselineData = useMemo(() => {
    console.log('🔍 BASELINE DATA: Extracting data', {
      baselineDate,
      selectedKeyword,
      rankingHistoryLength: rankingHistory?.length,
      Inpdf
    });

    if (!selectedKeyword || !rankingHistory?.length) {
      return null;
    }

    // In PDF mode with exactly 2 snapshots, always use first as baseline
    if (Inpdf && rankingHistory.length === 2) {
      console.log('📌 BASELINE DATA: PDF mode - using first snapshot as baseline');
      const entry = rankingHistory[0];
      let keywordData = entry.keywords?.find(k => k.keyword === selectedKeyword) ||
                        entry.keywordMetrics?.find(k => k.keyword === selectedKeyword);

      if (!keywordData) {
        keywordData = entry.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase()) ||
                      entry.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
      }

      if (keywordData) {
        console.log('✅ BASELINE DATA: Found in first snapshot with', keywordData?.testLocations?.length || 0, 'locations');
        return { ...keywordData, date: entry.analyzedAt || entry.snapshotDate, isSimulation: entry.isSimulation };
      }
      return null;
    }

    if (!baselineDate) {
      return null;
    }

    // Regular mode: Find entry by date - normalize dates for reliable comparison
    const baselineNormalized = new Date(baselineDate).toISOString().split('.')[0];
    const entry = rankingHistory.find(entry => {
      const dateValue = entry.analyzedAt || entry.snapshotDate;
      const entryNormalized = new Date(dateValue).toISOString().split('.')[0];
      return entryNormalized === baselineNormalized;
    });

    if (!entry) {
      console.warn('❌ BASELINE: No entry found for date:', baselineDate);
      return null;
    }

    let keywordData = entry.keywords?.find(k => k.keyword === selectedKeyword) ||
                       entry.keywordMetrics?.find(k => k.keyword === selectedKeyword);

    if (!keywordData) {
      keywordData = entry.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase()) ||
                    entry.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
    }

    return keywordData ? { ...keywordData, date: entry.analyzedAt || entry.snapshotDate, isSimulation: entry.isSimulation } : null;
  }, [baselineDate, selectedKeyword, rankingHistory, Inpdf]);

  const comparisonData = useMemo(() => {
    console.log('🔍 COMPARISON DATA: Extracting data', {
      comparisonDate,
      selectedKeyword,
      rankingHistoryLength: rankingHistory?.length,
      Inpdf
    });

    if (!selectedKeyword || !rankingHistory?.length) {
      return null;
    }

    // In PDF mode with exactly 2 snapshots, always use second as comparison
    if (Inpdf && rankingHistory.length === 2) {
      console.log('📌 COMPARISON DATA: PDF mode - using second snapshot as comparison');
      const entry = rankingHistory[1];
      let keywordData = entry.keywords?.find(k => k.keyword === selectedKeyword) ||
                        entry.keywordMetrics?.find(k => k.keyword === selectedKeyword);

      if (!keywordData) {
        keywordData = entry.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase()) ||
                      entry.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
      }

      if (keywordData) {
        console.log('✅ COMPARISON DATA: Found in second snapshot with', keywordData?.testLocations?.length || 0, 'locations');
        return { ...keywordData, date: entry.analyzedAt || entry.snapshotDate, isSimulation: entry.isSimulation };
      }
      return null;
    }

    if (!comparisonDate) {
      return null;
    }

    // Regular mode: Find entry by date - normalize dates for reliable comparison
    const comparisonNormalized = new Date(comparisonDate).toISOString().split('.')[0];
    const entry = rankingHistory.find(entry => {
      const dateValue = entry.analyzedAt || entry.snapshotDate;
      const entryNormalized = new Date(dateValue).toISOString().split('.')[0];
      return entryNormalized === comparisonNormalized;
    });

    if (!entry) {
      console.warn('❌ COMPARISON: No entry found for date:', comparisonDate);
      return null;
    }

    let keywordData = entry.keywords?.find(k => k.keyword === selectedKeyword) ||
                       entry.keywordMetrics?.find(k => k.keyword === selectedKeyword);

    if (!keywordData) {
      keywordData = entry.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase()) ||
                    entry.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
    }

    return keywordData ? { ...keywordData, date: entry.analyzedAt || entry.snapshotDate, isSimulation: entry.isSimulation } : null;
  }, [comparisonDate, selectedKeyword, rankingHistory, Inpdf]);

  // Vergleichsstatistiken berechnen
  const comparisonStats = useMemo(() => {
    if (!baselineData || !comparisonData) return null;

    // Corrected: Only calculate avgRankDiff when both averageRank values exist and are valid
    const avgRankDiff = (baselineData.averageRank && !isNaN(baselineData.averageRank) && 
                        comparisonData.averageRank && !isNaN(comparisonData.averageRank))
      ? baselineData.averageRank - comparisonData.averageRank
      : null;

    // Lokale Änderungen berechnen
    const baselineLocations = baselineData.testLocations || [];
    const comparisonLocations = comparisonData.testLocations || [];
    
    let improvements = 0;
    let declines = 0;
    let unchanged = 0;

    baselineLocations.forEach(baseLoc => {
      const compLoc = comparisonLocations.find(loc => 
        Math.abs(loc.lat - baseLoc.lat) < 0.0001 && 
        Math.abs(loc.lng - baseLoc.lng) < 0.0001
      );

      if (compLoc) {
        // Corrected: Proper N/A and null ranking handling
        const isBaselineFound = baseLoc.rank && baseLoc.rank !== 'N/A' && !isNaN(baseLoc.rank);
        const isCurrentFound = compLoc.rank && compLoc.rank !== 'N/A' && !isNaN(compLoc.rank);

        if (isBaselineFound && isCurrentFound) {
          const diff = baseLoc.rank - compLoc.rank;
          if (diff > 0) improvements++;
          else if (diff < 0) declines++;
          else unchanged++;
        } else if (isBaselineFound && !isCurrentFound) {
          // Was found, now not found = decline
          declines++;
        } else if (!isBaselineFound && isCurrentFound) {
          // Was not found, now found = improvement
          improvements++;
        } else {
          // Both not found = unchanged
          unchanged++;
        }
      }
    });

    return {
      avgRankDiff,
      improvements,
      declines,
      unchanged,
      totalLocations: improvements + declines + unchanged
    };
  }, [baselineData, comparisonData]);

  if (!selectedKeyword) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CompareArrows sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Visueller Vergleichsmodus
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Wählen Sie ein Keyword aus der Liste, um zwei Zeiträume visuell zu vergleichen.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Only show "not enough data" if we have 0 snapshots. If we have 1, allow the user to select it
  // or create another one for comparison
  if (availableDates.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Keine Daten verfügbar
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Für das ausgewählte Keyword "{selectedKeyword}" existieren noch keine Messungen.
            Führen Sie eine neue Messung durch, um Vergleiche zu ermöglichen.
          </Typography>
          <Box
            component="button"
            onClick={onCreateSnapshot}
            disabled={loading}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              backgroundColor: 'primary.main',
              color: amDesignColors.white,
              border: 'none',
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'primary.dark' },
              '&:disabled': { opacity: 0.6, cursor: 'not-allowed' }
            }}
          >
            <CameraAlt sx={{ fontSize: '1rem' }} />
            Erste Messung durchführen
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Check if only 1 snapshot available (no comparison possible at all)
  const isSameSnapshot = baselineDate && comparisonDate && baselineDate === comparisonDate;
  const noComparison = availableDates.length === 1 || isSameSnapshot;

  if (availableDates.length === 0) {
    // No snapshots available at all
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Schedule sx={{ fontSize: 48, color: 'primary.main', mb: 2, opacity: 0.7 }} />
          <Typography variant="h6" gutterBottom>
            Keine Messungen verfügbar
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Für das Keyword "{selectedKeyword}" sind keine Messungen verfügbar.
          </Typography>
          <Box
            component="button"
            onClick={onCreateSnapshot}
            disabled={loading}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 1.5,
              backgroundColor: 'primary.main',
              color: amDesignColors.white,
              border: 'none',
              borderRadius: 1,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: 'primary.dark' },
              '&:disabled': { opacity: 0.6, cursor: 'not-allowed' }
            }}
          >
            <CameraAlt sx={{ fontSize: '1.2rem' }} />
            Neue Messung durchführen
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{
      height: Inpdf ? '950px' : '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header mit Zeitauswahl */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, fontSize: '1.1rem' }}>
                Visueller Vergleichsmodus
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Keyword: <strong>{selectedKeyword}</strong> • Direkte Kartenvergleiche
              </Typography>
            </Box>

            {!Inpdf && (
              <Box
                component="button"
                onClick={onCreateSnapshot}
                disabled={loading}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: 'primary.main',
                  color: amDesignColors.white,
                  border: 'none',
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' }
                }}
              >
                <CameraAlt sx={{ fontSize: '1rem' }} />
                Neue Messung
              </Box>
            )}

          </Box>

          {/* Zeitauswahl-Controls */}

          {!Inpdf && (
            <Box display="flex" gap={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel> Baseline</InputLabel>
                <Select
                  value={baselineDate}
                  label=" Baseline"
                  onChange={(e) => setBaselineDate(e.target.value)}
                >
                  {availableDates.map((date) => (
                    <MenuItem key={date.value} value={date.value}>
                      {new Date(date.value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                      {date.isSimulation && (
                        <Chip
                          label="Sim"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel> Vergleich</InputLabel>
                <Select
                  value={comparisonDate}
                  label=" Vergleich"
                  onChange={(e) => setComparisonDate(e.target.value)}
                >
                  {availableDates.map((date) => (
                    <MenuItem key={date.value} value={date.value}>
                      {new Date(date.value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                      {date.isSimulation && (
                        <Chip
                          label="Sim"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

        </CardContent>
      </Card>

      {noComparison && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            ⚠️ Baseline und Vergleich-Snapshot sind identisch
          </Typography>
          <Typography variant="caption" color="inherit">
            Es stehen nicht genug Snapshots für einen Vergleich zur Verfügung. Die Baseline und Vergleich-Karten zeigen dieselben Daten.
          </Typography>
        </Alert>
      )}

      {isMobile && (
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>

 


          <ToggleButtonGroup
            value={mobileView}
            exclusive
            onChange={(_, newView) => newView && setMobileView(newView)}
            size="small"
            fullWidth
          >
            <ToggleButton value="baseline">
              Baseline({new Date(baselineDate).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' })})
            </ToggleButton>
            <ToggleButton value="comparison">
              Vergleich ({new Date(comparisonDate).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' })})
            </ToggleButton>
          </ToggleButtonGroup> 



        </Box>
      )}

      {/* Karten-Container */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'hidden',
        mb: 1.5,
        minHeight: Inpdf ? '500px' : 'auto'
      }}>
        {/* Baseline Karte */}
        <Box sx={{ 
          flex: 1,
          display: isMobile && mobileView !== 'baseline' ? 'none' : 'flex',
          flexDirection: 'column',
          borderRight: !isMobile ? '1px solid' : 'none',
          borderColor: 'divider'
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}>





{!Inpdf ? (
              <>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Baseline ({baselineData?.averageRank ? Math.round(baselineData.averageRank) : 'N/A'})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {baselineData && new Date(baselineData.date).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  {baselineData?.isSimulation && ' (Sim)'}
                </Typography>
              </>
            ) : (
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                Vor
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, p: 1, minHeight: Inpdf ? '550px' : '100%' }}>
            {baselineData ? (
              <ComparisonMap
                keywordData={baselineData}
                businessLocation={businessLocation}
                title="Baseline"
                mapId="baseline-map"
                Inpdf={Inpdf}
                comparisonData={comparisonData}
              />
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: amDesignColors.ghost,
                borderRadius: 1
              }}>
                <Box textAlign="center">
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    📅 Keyword nicht in Baseline
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Das Keyword "{selectedKeyword}" existiert nicht in dieser Periode.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Vergleichs Karte */}
        <Box sx={{ 
          flex: 1,
          display: isMobile && mobileView !== 'comparison' ? 'none' : 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            backgroundColor: 'blue.50'
          }}>




            {!Inpdf ? (
              <>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Vergleich ({comparisonData?.averageRank ? Math.round(comparisonData.averageRank) : 'N/A'})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {comparisonData && new Date(comparisonData.date).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  {comparisonData?.isSimulation && ' (Sim)'}
                </Typography>
              </>
            ) : (
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                Nach
              </Typography>
            )}

          </Box>
          <Box sx={{ flex: 1, p: 1, minHeight: Inpdf ? '550px' : '100%' }}>
            {comparisonData ? (
              <ComparisonMap
                keywordData={comparisonData}
                businessLocation={businessLocation}
                title="Vergleich"
                mapId="comparison-map"
                baselineData={baselineData}
                comparisonData={comparisonData}
                Inpdf={Inpdf}
              />
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: amDesignColors.ghost,
                borderRadius: 1
              }}>
                <Box textAlign="center">
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    📊 Keyword nicht verfügbar
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Das Keyword "{selectedKeyword}" existiert nicht in dieser Periode.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Kompakte Statistiken unter den Karten */}
      {comparisonStats && baselineData && comparisonData && (
        <Card>
          <CardContent sx={{ p: 1.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              {/* Trend */}
              <Box display="flex" alignItems="center" gap={1}>
{ comparisonStats.improvements >0 ?

  <>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor:amDesignColors.success ,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: amDesignColors.white,
                    fontSize: '24px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                  }}
                >
                  { '↗'}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    { comparisonStats.improvements> 0 ? `${Math.round(Math.abs(comparisonStats.improvements))} Plätze besser `: '' }
                    </Typography>

                </Box>

                </> : ' '
}




 { !Inpdf ? ( comparisonStats.declines >0 ?

  <>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor:amDesignColors.error ,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: amDesignColors.white,
                    fontSize: '24px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)'
                  }}
                >
                  { '↘'}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {  `${Math.round(Math.abs(comparisonStats.declines))} Plätze schlechter `}
                    </Typography>

                </Box>

                </> : ' '):null
}     



              </Box>

              {!Inpdf ?(<Typography variant="caption" color="text.secondary" fontWeight={700}>
                    {Math.abs(Math.ceil((new Date(comparisonDate) - new Date(baselineDate)) / (1000 * 60 * 60 * 24)))} Tage Zeitraum
                  </Typography>) :null }
                

              {/* Statistiken Chips */}

              { !Inpdf ? (
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={`${comparisonStats.improvements}↗`}
                  color="success"
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
                <Chip
                  label={`${comparisonStats.unchanged}—`}
                  color="default"
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
                <Chip
                  label={`${comparisonStats.declines}↘`}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </Box>) : null

              }
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SplitMapComparison;